// v49.1 — Auth helpers.
// Ports v48.306 (bullet-proof shellSignOut) and v48.308 (lcClearEncryptedTables
// emergency recovery helper). Both are exposed on window so the v48.x
// DevTools console diagnostics still work during the migration window.

import { supabase } from "@/lib/api/supabase-client";
import { clearTokens } from "@/lib/api/client";
import { clearSessionKey } from "@/lib/crypto";

/* v48.308 — Emergency recovery helper. Clears the four encrypted-table
   localStorage keys (mailboxes / notifications / disputes / siteOps) + their
   :archive siblings. Used by shellSignOut on the sign-out path AND can be
   called directly from the DevTools console when a user lands in the
   "garbled enc:..." state because of orphan blobs. */
export function lcClearEncryptedTables(): number {
  const encryptedTables = ["mailboxes", "notifications", "disputes", "siteOps"];
  const dbKey = "labourcore_tt_v1";
  let cleared = 0;
  encryptedTables.forEach(t => {
    try { if (localStorage.getItem(dbKey + ":" + t))            { localStorage.removeItem(dbKey + ":" + t); cleared++; } } catch (_) { /* ignore */ }
    try { if (localStorage.getItem(dbKey + ":" + t + ":archive")) { localStorage.removeItem(dbKey + ":" + t + ":archive"); cleared++; } } catch (_) { /* ignore */ }
  });
  console.log("[lcClearEncryptedTables] cleared", cleared, "key(s)");
  return cleared;
}

/* v48.306 — Bullet-proof shellSignOut. Synchronously clears every known
   LabourCore + Supabase localStorage/sessionStorage key + drops the
   encrypted-table blobs (because the session key is being destroyed,
   v48.308) + dispatches lc-auth-changed + ends with location.replace("/")
   so no React state, listener, or timer can rehydrate. The hard
   navigation is the safety net — even if a useEffect somewhere tries to
   restore lc_subscription, the reload kills everything first.

   Logs every step with [shellSignOut] prefix so any future regression
   shows up cleanly in console. */
export function shellSignOut(): void {
  const log = (s: string) => { try { console.log("[shellSignOut]", s); } catch (_) { /* ignore */ } };
  log("called");
  try { (window as unknown as { __lc_signing_out: boolean }).__lc_signing_out = true; } catch (_) { /* ignore */ }

  /* Step 1 — Supabase signOut. Fire-and-forget; the reload kills any
     in-flight promises anyway. */
  try {
    void supabase.auth.signOut();
    log("Supabase signOut fired");
  } catch (e) { log("Supabase signOut threw: " + (e as Error).message); }

  /* Step 2 — Drop the crypto session key so a fresh tab starts clean. */
  try { clearSessionKey(); log("crypto session key cleared"); } catch (_) { /* ignore */ }

  /* Step 3 — Sweep every known LabourCore + Supabase storage key. */
  try {
    const explicit = [
      "lc_subscription",
      "lc_session_key", "lc_session", "lc_user_v1", "lc_journey_v1",
      "lc_hybrid_active_role_v1",
      "lc_admin_view_platform",
      "lc_signup_intent",
    ];
    explicit.forEach(k => {
      try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
      try { sessionStorage.removeItem(k); } catch (_) { /* ignore */ }
    });
    Object.keys(localStorage).filter(k => k.startsWith("sb-") || k.startsWith("supabase.")).forEach(k => {
      try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
    });
    /* Drop orphan encrypted-table blobs at the same moment the session
       key is dropped (v48.308 fix). */
    lcClearEncryptedTables();
    log("localStorage + sessionStorage + encrypted blobs cleared");
  } catch (e) { log("storage clear threw: " + (e as Error).message); }

  /* Step 4 — Dispatch same-tab auth-change event. Any subscriber
     (useShellUser, legacy mailbox listeners) re-reads. */
  try {
    window.dispatchEvent(new Event("lc-auth-changed"));
    log("lc-auth-changed dispatched");
  } catch (_) { /* ignore */ }

  /* Step 5 — Hard reload to "/". Kills all React state + listeners. */
  log("redirecting to / via location.replace");
  try { location.replace("/"); }
  catch (e) {
    log("location.replace threw — falling back: " + (e as Error).message);
    try { location.href = "/"; } catch (_) { /* ignore */ }
  }
}

/* Expose on window so any v48.x console diagnostic / emergency recovery
   path still finds these helpers. */
if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).shellSignOut          = shellSignOut;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).lcClearEncryptedTables = lcClearEncryptedTables;
}

/* v48.308 — Auto-cleanup at bootstrap: if sessionStorage has no
   crypto session key BUT localStorage holds encrypted blobs, the
   blobs are orphans we'll never decrypt. Drop them silently before
   the UI tries to render. Runs on module import. */
try {
  const hasSessionKey = sessionStorage.getItem("lc_session_key");
  const hasEncryptedBlob = ["mailboxes", "notifications", "disputes", "siteOps"]
    .some(t => {
      const raw = localStorage.getItem("labourcore_tt_v1:" + t);
      return raw && raw.indexOf("enc:") >= 0;
    });
  if (!hasSessionKey && hasEncryptedBlob) {
    console.warn("[bootstrap] Detected encrypted blobs without a session key — clearing orphans (v48.308 auto-cleanup).");
    lcClearEncryptedTables();
  }
} catch (_) { /* ignore */ }
